import { useEffect, useRef, useState } from 'react';

import { getSecurityQuestion, login, register, resetPassword } from '@/api/auth';
import { Button } from '@/components/ui/button/button';
import { Input } from '@/components/ui/input/input';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types';

import styles from './login.module.css';

/* ---------- 输入框左侧线性图标 ---------- */
const IconUser = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="3.6" />
    <path d="M4.5 20c1.2-3.4 4-5 7.5-5s6.3 1.6 7.5 5" />
  </svg>
);
const IconLock = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4.5" y="10" width="15" height="10" rx="2.5" />
    <path d="M8 10V7.5a4 4 0 0 1 8 0V10" />
    <circle cx="12" cy="15" r="1.3" />
  </svg>
);
const IconShield = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l7 2.6v5.1c0 4.6-2.9 8-7 10.3-4.1-2.3-7-5.7-7-10.3V5.6L12 3z" />
    <path d="M9 12l2.2 2.2L15.5 10" />
  </svg>
);

/* ---------- 验证码字符集（去混淆） ---------- */
const CAPTCHA_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXY23456789';

export function LoginPage() {
  const [mode, setMode] = useState<'login' | 'register' | 'forgot'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.STUDENT);
  const [captcha, setCaptcha] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const setAuth = useAuthStore((state) => state.setAuth);

  // 找回密码状态
  const [forgotStep, setForgotStep] = useState<'input' | 'answer' | 'success'>('input');
  const [securityQuestion, setSecurityQuestion] = useState<string | null>(null);
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const captchaCodeRef = useRef('');

  /* ---------- 本地图形验证码 ---------- */
  const drawCaptcha = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;

    let code = '';
    for (let i = 0; i < 4; i += 1) {
      code += CAPTCHA_CHARS[Math.floor(Math.random() * CAPTCHA_CHARS.length)];
    }
    captchaCodeRef.current = code;

    ctx.fillStyle = '#f3f7fe';
    ctx.fillRect(0, 0, w, h);

    for (let i = 0; i < 4; i += 1) {
      ctx.strokeStyle = `rgba(42, 111, 242, ${0.15 + Math.random() * 0.12})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(Math.random() * w, Math.random() * h);
      ctx.bezierCurveTo(
        Math.random() * w, Math.random() * h,
        Math.random() * w, Math.random() * h,
        Math.random() * w, Math.random() * h,
      );
      ctx.stroke();
    }

    const colors = ['#2a6ff2', '#5c8ef5', '#16a34a', '#f59e0b'];
    for (let i = 0; i < 4; i += 1) {
      ctx.save();
      ctx.font = `700 ${20 + Math.random() * 3}px 'Segoe UI', Arial, sans-serif`;
      ctx.fillStyle = colors[Math.floor(Math.random() * colors.length)];
      ctx.translate(12 + i * 24, h / 2 + 6);
      ctx.rotate((Math.random() - 0.5) * 0.5);
      ctx.fillText(code[i], 0, 0);
      ctx.restore();
    }

    for (let i = 0; i < 14; i += 1) {
      ctx.fillStyle = `rgba(91, 102, 117, ${0.15 + Math.random() * 0.15})`;
      ctx.beginPath();
      ctx.arc(Math.random() * w, Math.random() * h, 1, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  useEffect(() => {
    drawCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'forgot') {
      if (forgotStep === 'input') {
        await handleForgotQuery();
      } else if (forgotStep === 'answer') {
        await handleForgotReset();
      }
      return;
    }

    // 校验图形验证码（大小写不敏感）
    if (captcha.trim().toUpperCase() !== captchaCodeRef.current) {
      setError('验证码不正确，请重新输入');
      setCaptcha('');
      drawCaptcha();
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        const result = await login({ username, password });
        setAuth(result.user, result.token);
      } else {
        const result = await register({ username, password, display_name: displayName, role });
        setAuth(result.user, result.token);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '操作失败');
      setCaptcha('');
      drawCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleForgotQuery = async () => {
    if (!username.trim()) {
      setError('请输入账号');
      return;
    }
    setLoading(true);
    try {
      const result = await getSecurityQuestion(username.trim());
      if (!result.has_security_question || !result.security_question) {
        setError('该账号未设置密保，无法通过密保找回密码');
        return;
      }
      setSecurityQuestion(result.security_question);
      setForgotStep('answer');
    } catch (err) {
      setError(err instanceof Error ? err.message : '查询密保问题失败');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotReset = async () => {
    if (!securityAnswer.trim()) {
      setError('请输入密保答案');
      return;
    }
    if (newPassword.length < 6) {
      setError('新密码至少 6 位');
      return;
    }
    setLoading(true);
    try {
      await resetPassword({
        username: username.trim(),
        security_answer: securityAnswer.trim(),
        new_password: newPassword,
      });
      setForgotStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : '重置密码失败');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (target?: 'login' | 'register' | 'forgot') => {
    setError('');
    setCaptcha('');
    drawCaptcha();
    setMode(target ?? (mode === 'login' ? 'register' : 'login'));
    if (target !== 'forgot') {
      setForgotStep('input');
      setSecurityQuestion(null);
      setSecurityAnswer('');
      setNewPassword('');
    }
  };

  return (
    <div className={styles.container}>
      {/* ============ 背景艺术层（低干扰水印） ============ */}

      {/* 中部偏右：柔和蓝紫渐变光晕 */}
      <div className={`${styles.glow} ${styles.glowMain}`} aria-hidden="true" />
      <div className={`${styles.glow} ${styles.glowSub}`} aria-hidden="true" />

      {/* 左侧：淡蓝 AI 网络节点水印 */}
      <svg
        className={`${styles.net} ${styles.netLeft}`}
        viewBox="0 0 420 560"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g stroke="#2a6ff2" strokeWidth="1">
          <path d="M40 90 L150 40 L260 95 L330 45" />
          <path d="M40 90 L120 190 L260 95" />
          <path d="M120 190 L60 300 L180 350" />
          <path d="M260 95 L340 200 L180 350 L90 470" />
          <path d="M340 200 L410 280" />
          <path d="M180 350 L300 430 L220 520" />
          <path d="M90 470 L30 400" />
          <path d="M300 430 L390 500" />
        </g>
        <g fill="#2a6ff2">
          <circle cx="40" cy="90" r="4" />
          <circle cx="150" cy="40" r="3" />
          <circle cx="260" cy="95" r="5" />
          <circle cx="330" cy="45" r="3" />
          <circle cx="120" cy="190" r="4" />
          <circle cx="60" cy="300" r="3" />
          <circle cx="180" cy="350" r="6" />
          <circle cx="340" cy="200" r="4" />
          <circle cx="410" cy="280" r="3" />
          <circle cx="90" cy="470" r="4" />
          <circle cx="300" cy="430" r="5" />
          <circle cx="220" cy="520" r="3" />
          <circle cx="30" cy="400" r="3" />
          <circle cx="390" cy="500" r="4" />
        </g>
        <g fill="none" stroke="#2a6ff2" strokeWidth="1">
          <circle cx="180" cy="350" r="12" />
          <circle cx="260" cy="95" r="10" />
          <circle cx="300" cy="430" r="9" />
        </g>
      </svg>

      {/* 右下角：淡蓝 AI 网络节点水印 */}
      <svg
        className={`${styles.net} ${styles.netBr}`}
        viewBox="0 0 360 320"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g stroke="#2a6ff2" strokeWidth="1">
          <path d="M30 260 L110 200 L210 240 L300 170" />
          <path d="M110 200 L140 110 L230 90" />
          <path d="M210 240 L250 300 L330 270" />
          <path d="M140 110 L60 70" />
          <path d="M230 90 L300 170 L330 270" />
        </g>
        <g fill="#2a6ff2">
          <circle cx="30" cy="260" r="4" />
          <circle cx="110" cy="200" r="5" />
          <circle cx="210" cy="240" r="4" />
          <circle cx="300" cy="170" r="6" />
          <circle cx="140" cy="110" r="3" />
          <circle cx="230" cy="90" r="4" />
          <circle cx="250" cy="300" r="3" />
          <circle cx="330" cy="270" r="4" />
          <circle cx="60" cy="70" r="3" />
        </g>
        <g fill="none" stroke="#2a6ff2" strokeWidth="1">
          <circle cx="300" cy="170" r="11" />
          <circle cx="110" cy="200" r="9" />
        </g>
      </svg>

      {/* 右上角：半透明作业纸张水印（轮廓 + 横线 + 红笔对勾 + 红色批注） */}
      <svg
        className={styles.paper}
        viewBox="0 0 300 380"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g transform="rotate(6 150 190)">
          <rect x="55" y="40" width="190" height="250" rx="10" stroke="#5b6472" strokeWidth="1.5" className={styles.paperInk} />
          <g stroke="#5b6472" strokeWidth="1" strokeLinecap="round" className={styles.paperInk}>
            <path d="M78 80 H222" />
            <path d="M78 112 H222" />
            <path d="M78 144 H206" />
            <path d="M78 176 H222" />
            <path d="M78 208 H190" />
            <path d="M78 240 H214" />
          </g>
          <path
            d="M92 168 L108 190 L142 138"
            stroke="#ef4444"
            strokeWidth="5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={styles.paperRed}
          />
          <circle cx="212" cy="76" r="7" stroke="#ef4444" strokeWidth="2.5" className={styles.paperRed} />
          <path d="M186 226 q6 -8 12 0 q6 8 12 0" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" className={styles.paperRed} />
          <path d="M160 100 l16 12 M176 100 l-16 12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" className={styles.paperRed} />
        </g>
      </svg>

      {/* 左上：铅笔线性图标水印（教师批阅） */}
      <svg
        className={`${styles.floatIcon} ${styles.fiPen}`}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M4 20l1.2-4.2L15.8 5.2a2 2 0 0 1 2.8 0l.2.2a2 2 0 0 1 0 2.8L8.2 18.8 4 20z"
          stroke="#2a6ff2"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <path d="M14.5 6.5l3 3" stroke="#2a6ff2" strokeWidth="1.4" />
      </svg>

      {/* 左下：柱状图线性图标水印（学情分析） */}
      <svg
        className={`${styles.floatIcon} ${styles.fiChart}`}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path d="M4 4v16h16" stroke="#7c6ff0" strokeWidth="1.4" strokeLinecap="round" />
        <rect x="7.5" y="12" width="3" height="5" rx="0.8" stroke="#7c6ff0" strokeWidth="1.4" />
        <rect x="13" y="8" width="3" height="9" rx="0.8" stroke="#7c6ff0" strokeWidth="1.4" />
        <rect x="18.5" y="10" width="0" height="0" />
        <path d="M7.5 8.5 L10.5 6.5 L13.5 9.5" stroke="#16a34a" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>

      {/* 右中：AI 对话气泡线性图标水印（智能反馈） */}
      <svg
        className={`${styles.floatIcon} ${styles.fiChat}`}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H10l-4.5 4v-4.2A2.5 2.5 0 0 1 4 13.5v-7z"
          stroke="#2a6ff2"
          strokeWidth="1.4"
          strokeLinejoin="round"
        />
        <path d="M8.5 10.2 L10.6 12.3 L14.8 8.2" stroke="#2a6ff2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>

      {/* 几何点缀：细描边圆环 + 加号 */}
      <span className={`${styles.geo} ${styles.geoRing}`} aria-hidden="true" />
      <span className={`${styles.geo} ${styles.geoPlus}`} aria-hidden="true">+</span>

      {/* ===== 知识元素水印 ===== */}

      {/* 顶部中左：打开的书本 */}
      <svg
        className={`${styles.floatIcon} ${styles.fiBook}`}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M12 6.5C10 4.9 7.3 4.3 4 4.6v12.8c3.3-.3 6 .3 8 2 2-1.7 4.7-2.3 8-2V4.6c-3.3-.3-6 .3-8 1.9z"
          stroke="#2a6ff2"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
        <path d="M12 6.5v12.9" stroke="#2a6ff2" strokeWidth="1.3" />
        <path
          d="M6.3 8.3c1.5 0 2.9.3 4.1 1M6.3 11.2c1.5 0 2.9.3 4.1 1M13.6 9.3c1.2-.7 2.6-1 4.1-1M13.6 12.2c1.2-.7 2.6-1 4.1-1"
          stroke="#2a6ff2"
          strokeWidth="1"
          strokeLinecap="round"
        />
      </svg>

      {/* 右中下：毕业帽 */}
      <svg
        className={`${styles.floatIcon} ${styles.fiCap}`}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M12 3.6L22 8.2 12 12.8 2 8.2 12 3.6z"
          stroke="#7c6ff0"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
        <path
          d="M6.4 10.5v4.4c0 1.6 2.5 2.9 5.6 2.9s5.6-1.3 5.6-2.9v-4.4"
          stroke="#7c6ff0"
          strokeWidth="1.3"
        />
        <path d="M20.2 9.4v4.4" stroke="#7c6ff0" strokeWidth="1.3" strokeLinecap="round" />
        <circle cx="20.2" cy="15" r="1.1" fill="#7c6ff0" />
      </svg>

      {/* 左下：灯泡（知识灵感） */}
      <svg
        className={`${styles.floatIcon} ${styles.fiBulb}`}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M12 3.2a6.2 6.2 0 0 1 3.7 11.2c-.8.6-1.2 1.3-1.2 2.2H9.5c0-.9-.4-1.6-1.2-2.2A6.2 6.2 0 0 1 12 3.2z"
          stroke="#f59e0b"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
        <path d="M9.5 18.8h5M10.4 21.3h3.2" stroke="#f59e0b" strokeWidth="1.3" strokeLinecap="round" />
        <path
          d="M2.2 12h2M19.8 12h2M4.6 5.4L6 6.8M17.4 6.8l1.4-1.4M4.6 18.6L6 17.2M17.4 17.2l1.4 1.4"
          stroke="#f59e0b"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </svg>

      {/* 顶部中右：正弦函数曲线（数学） */}
      <svg
        className={`${styles.floatIcon} ${styles.fiSine}`}
        viewBox="0 0 76 46"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path d="M6 4v34h62" stroke="#7c6ff0" strokeWidth="1.1" strokeLinecap="round" />
        <path
          d="M8 23C14 9 20 9 26 23s12 14 18 0 12-14 18 0"
          stroke="#7c6ff0"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <text x="40" y="43.5" fontSize="8.5" fill="#7c6ff0" textAnchor="middle" fontFamily="Georgia, serif" fontStyle="italic">
          y = sin x
        </text>
      </svg>

      {/* 底部中左：三角尺（几何） */}
      <svg
        className={`${styles.floatIcon} ${styles.fiRuler}`}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M4.6 19.4h14.8L4.6 5.2v14.2z"
          stroke="#16a34a"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
        <path d="M9.6 15.6h5L9.6 10.9v4.7z" stroke="#16a34a" strokeWidth="1" strokeLinejoin="round" />
        <circle cx="7.6" cy="17" r="0.8" stroke="#16a34a" strokeWidth="0.9" />
        <circle cx="10.4" cy="17" r="0.8" stroke="#16a34a" strokeWidth="0.9" />
        <circle cx="13.2" cy="17" r="0.8" stroke="#16a34a" strokeWidth="0.9" />
      </svg>

      {/* 数学符号水印：π / ∑ / √ / ∞ */}
      <span className={`${styles.mathSym} ${styles.symPi}`} aria-hidden="true">π</span>
      <span className={`${styles.mathSym} ${styles.symSum}`} aria-hidden="true">∑</span>
      <span className={`${styles.mathSym} ${styles.symSqrt}`} aria-hidden="true">√</span>
      <span className={`${styles.mathSym} ${styles.symInf}`} aria-hidden="true">∞</span>

      {/* 勾股定理公式水印 */}
      <span className={`${styles.mathFormula}`} aria-hidden="true">a² + b² = c²</span>

      {/* A+ 批阅印章水印 */}
      <span className={`${styles.gradeMark}`} aria-hidden="true">A+</span>


      {/* 底部：很淡的点阵纹理 */}
      <div className={styles.dots} aria-hidden="true" />

      {/* ============ 登录卡片（第一视觉焦点） ============ */}
      <div className={styles.card}>
        <div className={styles.brandBadge} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4 5.5C4 4.67 4.67 4 5.5 4H11V20H5.5C4.67 20 4 19.33 4 18.5V5.5Z"
              fill="#2a6ff2"
              fillOpacity="0.16"
              stroke="#2a6ff2"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
            <path
              d="M13 4H18.5C19.33 4 20 4.67 20 5.5V18.5C20 19.33 19.33 20 18.5 20H13V4Z"
              fill="#2a6ff2"
              fillOpacity="0.08"
              stroke="#2a6ff2"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
            <path d="M7.2 9.2 L9 11 L12 7.2" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15 8 H18 M15 11 H17.5 M15 14 H18" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>

        <h1 className={styles.title}>AI 智能批阅与反馈平台</h1>
        <p className={styles.subtitle}>
          {mode === 'login' && '教师智能批阅工作台，让作业反馈更高效'}
          {mode === 'register' && '创建账号，开启智能批阅体验'}
          {mode === 'forgot' && '通过密保问题找回账号密码'}
        </p>

        {mode === 'forgot' && forgotStep === 'success' ? (
          <div className={styles.form}>
            <div className={styles.successBox}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <p>密码重置成功，请使用新密码登录</p>
            </div>
            <Button
              type="button"
              fullWidth
              className={styles.loginBtn}
              onClick={() => {
                setPassword(newPassword);
                switchMode('login');
              }}
            >
              去登录
            </Button>
          </div>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit}>
            <Input
              label="账号"
              leftIcon={IconUser}
              placeholder="请输入账号（3-32 位）"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              autoComplete="username"
              disabled={mode === 'forgot' && forgotStep === 'answer'}
            />

            {mode !== 'forgot' && (
              <Input
                label="密码"
                leftIcon={IconLock}
                type="password"
                placeholder={mode === 'register' ? '请输入密码（至少 6 位）' : '请输入密码'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === 'register' ? 6 : undefined}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              />
            )}

            {mode === 'forgot' && forgotStep === 'answer' && (
              <>
                <div className={styles.questionBox}>
                  <span className={styles.questionLabel}>密保问题</span>
                  <span className={styles.questionText}>{securityQuestion}</span>
                </div>
                <Input
                  label="密保答案"
                  leftIcon={IconShield}
                  placeholder="请输入密保答案"
                  value={securityAnswer}
                  onChange={(e) => setSecurityAnswer(e.target.value)}
                  required
                  autoComplete="off"
                />
                <Input
                  label="新密码"
                  leftIcon={IconLock}
                  type="password"
                  placeholder="请设置新密码（至少 6 位）"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </>
            )}

            {mode === 'register' && (
              <Input
                label="显示名称"
                placeholder="如：李老师 / 王同学"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            )}

            {mode === 'register' && (
              <label className={styles.selectLabel}>
                注册身份
                <select
                  className={styles.select}
                  value={role}
                  onChange={(e) => setRole(e.target.value as UserRole)}
                >
                  <option value={UserRole.TEACHER}>教师</option>
                  <option value={UserRole.STUDENT}>学生</option>
                </select>
              </label>
            )}

            {mode !== 'forgot' && (
              <div className={styles.captchaRow}>
                <Input
                  label="验证码"
                  leftIcon={IconShield}
                  placeholder="请输入验证码"
                  className={styles.captchaInput}
                  value={captcha}
                  onChange={(e) => setCaptcha(e.target.value)}
                  maxLength={4}
                  required
                />
                <canvas
                  ref={canvasRef}
                  className={styles.captchaCanvas}
                  width={108}
                  height={42}
                  title="点击刷新验证码"
                  onClick={drawCaptcha}
                />
              </div>
            )}

            {error && <div className={styles.error}>{error}</div>}
            <Button type="submit" fullWidth className={styles.loginBtn} loading={loading}>
              {mode === 'login' && '登 录'}
              {mode === 'register' && '注册并登录'}
              {mode === 'forgot' && (forgotStep === 'input' ? '下一步' : '重置密码')}
            </Button>
          </form>
        )}

        <div className={styles.switch}>
          {mode === 'login' ? (
            <span>
              还没有账号？
              <button type="button" className={styles.switchBtn} onClick={() => switchMode('register')}>
                立即注册
              </button>
              <span className={styles.divider}>|</span>
              <button type="button" className={styles.switchBtn} onClick={() => switchMode('forgot')}>
                忘记密码
              </button>
            </span>
          ) : mode === 'register' ? (
            <span>
              已有账号？
              <button type="button" className={styles.switchBtn} onClick={() => switchMode('login')}>
                去登录
              </button>
            </span>
          ) : (
            <span>
              想起密码了？
              <button type="button" className={styles.switchBtn} onClick={() => switchMode('login')}>
                去登录
              </button>
            </span>
          )}
        </div>

        {mode === 'login' && (
          <div className={styles.hint}>
            教师演示账号：teacher1 / password123；学生演示账号：student1 / password123
          </div>
        )}
      </div>


    </div>
  );
}
